import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  translations,
  SUPPORTED_LOCALES,
  FALLBACK_CHAIN,
  DEFAULT_LOCALE,
  LOCALE_META,
  type Locale,
  type TranslationKey,
} from './translations';

interface I18nContextType {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
  formatCurrency: (amount: number) => string;
  formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string;
  localePath: (path: string) => string;
  qaMode: boolean;
  setQaMode: (on: boolean) => void;
}

const I18nContext = createContext<I18nContextType | null>(null);

/** Map browser language tag to our supported locale */
const mapBrowserLocale = (tag: string): Locale | null => {
  const lower = tag.toLowerCase();
  // Exact match first
  if (SUPPORTED_LOCALES.includes(lower as Locale)) return lower as Locale;
  // en-* → en-gb
  if (lower.startsWith('en')) return 'en-gb';
  // Check 2-letter prefix
  const prefix = lower.slice(0, 2);
  const match = SUPPORTED_LOCALES.find(l => l.startsWith(prefix));
  return match || null;
};

export const detectLocale = (): Locale => {
  // 1. localStorage
  const stored = localStorage.getItem('locale');
  if (stored && SUPPORTED_LOCALES.includes(stored as Locale)) return stored as Locale;

  // 2. Cookie
  const cookie = document.cookie.split(';').find(c => c.trim().startsWith('locale='));
  if (cookie) {
    const val = cookie.split('=')[1]?.trim();
    if (val && SUPPORTED_LOCALES.includes(val as Locale)) return val as Locale;
  }

  // 3. Browser languages
  for (const lang of navigator.languages || [navigator.language]) {
    const mapped = mapBrowserLocale(lang);
    if (mapped) return mapped;
  }

  return DEFAULT_LOCALE;
};

/** Extract locale from URL pathname */
export const getLocaleFromPath = (pathname: string): Locale | null => {
  const seg = pathname.split('/')[1]?.toLowerCase();
  if (seg && SUPPORTED_LOCALES.includes(seg as Locale)) return seg as Locale;
  return null;
};

/** Remove locale prefix from path */
export const stripLocalePath = (pathname: string): string => {
  const seg = pathname.split('/')[1]?.toLowerCase();
  if (seg && SUPPORTED_LOCALES.includes(seg as Locale)) {
    return pathname.slice(seg.length + 1) || '/';
  }
  return pathname;
};

const persistLocale = (l: Locale) => {
  localStorage.setItem('locale', l);
  document.cookie = `locale=${l};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;
  document.documentElement.lang = LOCALE_META[l].htmlLang;
};

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine locale: URL > stored preference > browser
  const urlLocale = getLocaleFromPath(location.pathname);
  const initialLocale = urlLocale || detectLocale();

  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const [qaMode, setQaMode] = useState(false);

  // Redirect to locale-prefixed route if not present
  useEffect(() => {
    if (!urlLocale) {
      const detected = detectLocale();
      const stripped = stripLocalePath(location.pathname);
      navigate(`/${detected}${stripped === '/' ? '' : stripped}${location.search}${location.hash}`, { replace: true });
    }
  }, []);

  // Sync locale from URL on navigation
  useEffect(() => {
    const pathLocale = getLocaleFromPath(location.pathname);
    if (pathLocale && pathLocale !== locale) {
      setLocaleState(pathLocale);
      persistLocale(pathLocale);
    }
  }, [location.pathname]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    persistLocale(l);
    // Navigate to new locale
    const stripped = stripLocalePath(location.pathname);
    navigate(`/${l}${stripped === '/' ? '' : stripped}${location.search}${location.hash}`);
  }, [navigate, location]);

  const t = useCallback((key: TranslationKey, vars?: Record<string, string | number>): string => {
    // QA mode: show keys
    if (qaMode) return `[${key}]`;

    // Try locale → en-gb → fr
    let str = translations[locale]?.[key];
    if (!str) {
      for (const fb of FALLBACK_CHAIN) {
        str = translations[fb]?.[key];
        if (str) break;
      }
    }
    if (!str) {
      if (import.meta.env.DEV) {
        console.warn(`[i18n] Missing key "${key}" in all locales`);
      }
      return key;
    }

    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        str = str!.replace(`{${k}}`, String(v));
      });
    }
    return str;
  }, [locale, qaMode]);

  const formatCurrency = useCallback((amount: number): string => {
    const htmlLang = LOCALE_META[locale].htmlLang;
    return new Intl.NumberFormat(htmlLang, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(amount);
  }, [locale]);

  const formatDate = useCallback((date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
    const htmlLang = LOCALE_META[locale].htmlLang;
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString(htmlLang, options || { day: 'numeric', month: 'long', year: 'numeric' });
  }, [locale]);

  const localePath = useCallback((path: string): string => {
    return `/${locale}${path === '/' ? '' : path}`;
  }, [locale]);

  useEffect(() => {
    document.documentElement.lang = LOCALE_META[locale].htmlLang;
  }, [locale]);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, formatCurrency, formatDate, localePath, qaMode, setQaMode }}>
      {children}
    </I18nContext.Provider>
  );
};

/** Safe fallback so components don't crash during HMR or before redirect */
const FALLBACK_I18N: I18nContextType = {
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key: TranslationKey) => {
    const str = translations[DEFAULT_LOCALE]?.[key];
    return str || key;
  },
  formatCurrency: (amount: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount),
  formatDate: (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('fr-FR');
  },
  localePath: (path: string) => `/${DEFAULT_LOCALE}${path === '/' ? '' : path}`,
  qaMode: false,
  setQaMode: () => {},
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  return ctx || FALLBACK_I18N;
};
